import { EASETECH } from "@/utilities/global-variables"

/** What the visitor was trying to do on the other organization's portal when the mismatch popup opened. */
export type PortalMismatchNextStep = "dashboard" | "login" | "register"

/** `openDarkModal({ type: "portal-mismatch", content })` payload. */
export type PortalMismatchContent = {
  /** Organization the signed-in user belongs to. */
  userOrgSlug: string
  /** Organization whose portal the visitor is on. */
  targetOrgSlug: string
  portal: string
  nextStep: PortalMismatchNextStep
}

/** Same slug `DashboardLayout` compares against the URL, so the two checks never disagree. */
export const getUserOrgSlug = (user: Maybe<MeQuery["me"]>): Maybe<string> =>
  user?.userProfile?.data?.attributes?.organization?.data?.attributes?.slug

/**
 * The browser holds one session at a time, and an account belongs to exactly one organization.
 * True when the signed-in user belongs to an organization other than `orgSlug`: that portal's
 * dashboard is closed to them, and signing in there ends their current session.
 */
export const isSignedInToOtherOrg = (user: Maybe<MeQuery["me"]>, orgSlug: Maybe<string>): boolean => {
  const userOrgSlug = getUserOrgSlug(user)
  return Boolean(userOrgSlug && orgSlug && userOrgSlug !== orgSlug)
}

/**
 * `openDarkModal({ type: "choose-portal", content })` payload. Without content (login), picking an organization
 * opens its home page; with `nextStep: "forgot-password"` it opens that organization's forgot-password form.
 */
export type ChoosePortalContent = {
  nextStep?: "forgot-password"
}

/** `openDarkModal({ type: "outgoing-only-portal", content })` payload. */
export type OutgoingOnlyPortalContent = {
  /** The outgoing-only organization the client tried to use. */
  orgSlug: string
}

/**
 * An `outgoing` organization only submits its own agents' applications and takes no incoming ones,
 * so its portal has nothing for a client: they apply through the Easetech portal instead.
 * Easetech is excluded — it is the portal clients are sent to.
 */
export const isClientOfOutgoingOnlyOrg = (user: Maybe<MeQuery["me"]>): boolean => {
  const org = user?.organization?.data?.attributes
  return Boolean(
    org?.slug &&
      org.slug !== EASETECH &&
      org.processingType === "outgoing" &&
      user?.role?.data?.attributes?.name?.toLowerCase() === "client"
  )
}

/** Navigation state that makes the auth page open the outgoing-only popup after a redirect. */
export const OUTGOING_ONLY_NOTICE = "outgoing-only-portal"

/** `openDarkModal({ type: "payment-overdue", content })` payload. */
export type PaymentOverdueContent = {
  /** The organization whose account has outstanding payments. */
  orgSlug: string
}

/**
 * The user's organization has outstanding payments and their role (anything but client) is blocked until it is
 * settled. The CMS decides (`me.paymentOverdueBlocked`): the flag itself is private and never reaches the browser.
 */
export const isBlockedByPaymentOverdue = (user: Maybe<MeQuery["me"]>): boolean => user?.paymentOverdueBlocked === true

/** Navigation state that makes the auth page open the outstanding-payments popup after a redirect. */
export const PAYMENT_OVERDUE_NOTICE = "payment-overdue"
